import {
  useMemo,
  type CSSProperties,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { cx } from '../cx'
import styles from './Card.module.css'

export type CardEdge = 'clean' | 'deckled'
export type CardMount = 'tape' | 'pin'

export interface CardProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType
  /** Torn top and bottom edges, like paper pulled from a pad. */
  edge?: CardEdge
  /** Seed for the torn edge so a card keeps the same tear on every render and device. */
  seed?: string
  /** Decorative tape strip or push pin at the top (hidden in forced colours). */
  mount?: CardMount
  /** The riso offset shadow in the theme's spot colour instead of ink. */
  spot?: boolean
  padding?: 'none' | 'md' | 'lg'
  children?: ReactNode
}

/** FNV-1a: a tiny stable string hash for seeding the deckle. */
function hash(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

function mulberry32(seed: number): () => number {
  let a = seed
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * A clip-path polygon with torn top and bottom edges (06 §8.3), computed once per seed.
 * Depth is in px so the tear looks the same on short and tall cards.
 */
export function deckleClipPath(seed: string, teeth = 36, depth = 5): string {
  const rng = mulberry32(hash(seed))
  const pts: string[] = []
  for (let i = 0; i <= teeth; i++)
    pts.push(`${((i / teeth) * 100).toFixed(2)}% ${(rng() * depth).toFixed(1)}px`)
  for (let i = teeth; i >= 0; i--) {
    pts.push(`${((i / teeth) * 100).toFixed(2)}% calc(100% - ${(rng() * depth).toFixed(1)}px)`)
  }
  return `polygon(${pts.join(', ')})`
}

/** Raised paper: the surface every piece of text in Galley sits on. */
export function Card({
  as: Tag = 'div',
  edge = 'clean',
  seed = 'card',
  mount,
  spot = false,
  padding = 'md',
  className,
  style,
  children,
  ...rest
}: CardProps) {
  const clip = useMemo(() => (edge === 'deckled' ? deckleClipPath(seed) : null), [edge, seed])
  const clipStyle: CSSProperties | undefined = clip ? { clipPath: clip } : undefined

  const decoration = mount && (
    <span className={cx(styles.mount, styles[mount])} aria-hidden="true" data-decor="" />
  )

  if (clip) {
    // clip-path also clips box-shadow, so the offset shadow is its own clipped layer.
    return (
      <Tag
        className={cx(styles.deckleWrap, spot && styles.spot, className)}
        style={style}
        {...rest}
      >
        <span className={styles.deckleShadow} style={clipStyle} aria-hidden="true" data-misreg="" />
        <div className={cx(styles.card, styles.deckled, styles[padding])} style={clipStyle}>
          {children}
        </div>
        {decoration}
      </Tag>
    )
  }

  return (
    <Tag
      className={cx(styles.card, styles.shadowed, spot && styles.spot, styles[padding], className)}
      style={style}
      {...rest}
    >
      {children}
      {decoration}
    </Tag>
  )
}
